import type { GenericRelationship } from 'tio-data/data/resources/generic-relationship.type';

/** The partner-side audiences the role-based dashboard is composed for. */
export type PartnerPersona =
  'accountOwner' | 'partnerAdmin' | 'supervisor' | 'approver' | 'pslfApprover';

/**
 * GenericRelationship enum keys, as `/me` serializes `relationshipType` on
 * jurisdictive relationships. The search index spells the PPP/TAP finance
 * approvers with a `TAS_` prefix; `/me` does not.
 */
export const PERSONA_RELATIONSHIP_TYPES: Readonly<Record<PartnerPersona, readonly string[]>> = {
  accountOwner: ['ACCOUNT_OWNER'],
  partnerAdmin: ['EMPLOYEE_ADMIN'],
  supervisor: ['SUPERVISOR', 'DEPARTMENT_HEAD'],
  approver: [
    'TAS_APPROVER',
    'TAS_FINANCE_APPROVER',
    'TAS_CUSTOM_APPROVER',
    'TAS_ADMIN_APPROVER',
    'TAS_CUSTOM_INDIVIDUAL_APPROVER',
    'PPP_FINANCE_APPROVER',
    'TAP_FINANCE_APPROVER',
  ],
  pslfApprover: ['PSLF_ADMIN', 'PSLF_GROUP_APPROVER'],
};

const EMPLOYMENT_SCOPED_GOVERNABLES = new Set(['Employee', 'PslfGroup']);

type RelationshipLike = Pick<
  GenericRelationship,
  'relationshipType' | 'governableType' | 'governableId' | 'isActive'
>;

/**
 * Resolves the viewer's partner personas from the jurisdictive relationships on
 * their current employment. Personas stack: an account owner who also approves
 * gets both.
 *
 * Only active rows count. Company-governed rows count only for `companyId`;
 * employee- and PSLF-group-governed rows (supervisor, individual approver, PSLF
 * group) already belong to this employment, so they are not company-checked.
 * Rows governing anything else are ignored.
 *
 * @param companyId the current company; when absent, company-governed rows never match
 */
export function resolvePartnerPersonas(
  relationships: readonly RelationshipLike[] | undefined,
  companyId: string | undefined
): Set<PartnerPersona> {
  const held = new Set(
    (relationships ?? [])
      .filter((row) => row.isActive)
      .filter((row) =>
        row.governableType === 'Company'
          ? row.governableId === companyId
          : EMPLOYMENT_SCOPED_GOVERNABLES.has(row.governableType)
      )
      .map((row) => row.relationshipType)
  );

  const personas = new Set<PartnerPersona>();
  for (const [persona, types] of Object.entries(PERSONA_RELATIONSHIP_TYPES)) {
    if (types.some((type) => held.has(type))) personas.add(persona as PartnerPersona);
  }
  return personas;
}
